// frontend/src/pages/portal/modifiers/ModifierGroupEditor.js
//
// Modal editor for one modifier group: name/receipt-name/selection-rule/
// required (explicit Save — text-form-field convention) and its ordered
// option list (auto-save on add/remove — list-operation convention).
//
// Nesting lives on the option's add-on, not on the group (Task 3's
// AddonModifierGroup table keys off addon_product_code) — so attaching or
// removing a nested choice calls modifierService.setAddonGroups() against
// that option's add-on code, immediately, same as any other list op.
//
// "Edit" on an already-nested option drills into that nested group by
// rendering a second instance of this same component on top — the depth
// cap is 2, so at most two of these can ever be stacked.
//
// Visual contract: docs/mockups/nested-addons/portal-group-builder.html
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { C, FONT } from '../../../styles/theme';
import { modifierService } from '../../../services/api';
import { ruleLabel, generateGroupId } from './modifierRules';
import NestedGroupPicker from './NestedGroupPicker';

const btnBase = { padding: '9px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none' };

export default function ModifierGroupEditor({ open, group, allGroups = [], addons = [], currency = '', onClose, onSaved, t, toast }) {
    const [form, setForm] = useState({ name: '', receiptName: '', maxSelect: 1, required: false });
    const [options, setOptions] = useState([]);
    const [groupId, setGroupId] = useState(null);
    const [savedGroup, setSavedGroup] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [addingOption, setAddingOption] = useState(false);
    const [pickerFor, setPickerFor] = useState(null); // { addonCode, addonName } | null
    const [nestedOverrides, setNestedOverrides] = useState({}); // addonCode -> group | null
    const [drillGroup, setDrillGroup] = useState(null); // group being edited one level deeper, or null
    const [drillForCode, setDrillForCode] = useState(null); // the option addon_product_code the drill was opened from

    useEffect(() => {
        if (!open) return;
        setForm({
            name: group?.name || '',
            receiptName: group?.receipt_name || '',
            maxSelect: group ? group.max_select : 1,
            required: (group?.min_select || 0) > 0,
        });
        setOptions(group?.options ? group.options.map(o => ({ ...o })) : []);
        setGroupId(group?.group_id || null);
        setSavedGroup(group || null);
        setNestedOverrides({});
        setConfirmingDelete(false);
        setAddingOption(false);
        setPickerFor(null);
        setDrillGroup(null);
        setDrillForCode(null);
    }, [open, group]);

    // Escape must close only the topmost active layer, same precedence as
    // the ✕/backdrop-click path. When a drill-in instance is open, its own
    // `window` keydown listener is *also* registered (it's a separate,
    // still-mounted ModifierGroupEditor) — if this outer handler didn't
    // bail out on `drillGroup`, a single Escape press would fire both:
    // the inner instance correctly closes itself, but this outer handler
    // would ALSO fire and close the whole outer editor, discarding any
    // unsaved outer text-field edits. Bailing here leaves the inner
    // instance's own handler to close just that layer (it calls this
    // instance's `onClose={() => setDrillGroup(null)}` — see the render
    // below), so repeated Escapes step back through the stack one layer
    // at a time.
    useEffect(() => {
        const onKey = e => {
            if (e.key !== 'Escape') return;
            if (drillGroup) return; // topmost layer is the drilled-in editor; let it handle this Escape
            if (pickerFor) { setPickerFor(null); return; }
            onClose && onClose();
        };
        if (open) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose, pickerFor, drillGroup]);

    const addonMap = useMemo(() => {
        const m = {};
        (addons || []).forEach(a => { m[a.product_code] = a; });
        return m;
    }, [addons]);

    // Every add-on that already carries a nested group somewhere in the
    // tenant's catalog — feeds both this group's own nested-option display
    // and the depth-guard greying handed to NestedGroupPicker.
    const nestedAddonCodes = useMemo(() => {
        const codes = new Set();
        (allGroups || []).forEach(g => {
            if (!g.nested_in) return;
            const match = (addons || []).find(a => a.name === g.nested_in);
            if (match) codes.add(match.product_code);
        });
        return codes;
    }, [allGroups, addons]);

    const nestedGroupFor = useCallback((code) => {
        if (Object.prototype.hasOwnProperty.call(nestedOverrides, code)) return nestedOverrides[code];
        const addon = addonMap[code];
        if (!addon) return null;
        return (allGroups || []).find(g => g.nested_in === addon.name) || null;
    }, [nestedOverrides, addonMap, allGroups]);

    // Candidate list for the nested-group picker, decorated with the
    // depth-guard flag NestedGroupPicker just renders. Computed here (not
    // after the early return below) because hooks can't be conditional.
    const pickerCandidates = useMemo(() => (allGroups || [])
        .filter(g => g.group_id !== groupId)
        .map(g => ({ ...g, tooDeep: (g.options || []).some(o => nestedAddonCodes.has(o.addon_product_code)) })),
    [allGroups, groupId, nestedAddonCodes]);

    if (!open) return null;

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const buildPayload = () => ({
        name: form.name.trim(),
        receipt_name: form.receiptName.trim() || null,
        min_select: form.required ? 1 : 0,
        max_select: form.maxSelect,
        sort_order: savedGroup?.sort_order || 0,
        translation_mode: savedGroup?.translation_mode || null,
        manual_translation: savedGroup?.manual_translation || null,
    });

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast && toast(t('group_name_required', {}, 'Group name is required'), 'error');
            return;
        }
        setSaving(true);
        try {
            const payload = buildPayload();
            let gid = groupId;
            if (!gid) {
                gid = generateGroupId(payload.name, allGroups);
                await modifierService.createGroup({ group_id: gid, ...payload });
                if (options.length > 0) {
                    await modifierService.setGroupOptions(
                        gid, options.map((o, i) => ({ addon_product_code: o.addon_product_code, sort_order: i })));
                }
                setGroupId(gid);
            } else {
                await modifierService.updateGroup(gid, payload);
            }
            toast && toast(t('group_saved', {}, 'Group saved'), 'success');
            onSaved && onSaved();
        } catch {
            toast && toast(t('failed_to_save_group', {}, 'Failed to save group'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!groupId) { onClose && onClose(); return; }
        setSaving(true);
        try {
            await modifierService.deleteGroup(groupId);
            toast && toast(t('group_deleted', {}, 'Group deleted'), 'success');
            onSaved && onSaved();
        } catch {
            toast && toast(t('group_in_use_cannot_delete', {}, "Can't delete — this group is still in use"), 'error');
        } finally {
            setSaving(false);
            setConfirmingDelete(false);
        }
    };

    // List operations (add/remove option) auto-save immediately once the
    // group exists on the server. For a brand-new, not-yet-created group
    // there's nothing to save against yet — the local list is sent along
    // with the initial createGroup call in handleSave.
    const persistOptions = async (next) => {
        setOptions(next);
        if (!groupId) return;
        try {
            await modifierService.setGroupOptions(
                groupId, next.map((o, i) => ({ addon_product_code: o.addon_product_code, sort_order: i })));
        } catch {
            toast && toast(t('failed_to_save_group', {}, 'Failed to save group'), 'error');
        }
    };

    const addOption = (code) => {
        if (options.some(o => o.addon_product_code === code)) return;
        persistOptions([...options, { addon_product_code: code, sort_order: options.length }]);
        setAddingOption(false);
    };

    const removeOption = (code) => {
        persistOptions(options.filter(o => o.addon_product_code !== code));
    };

    const openNestPicker = (code) => setPickerFor({ addonCode: code, addonName: addonMap[code]?.name || code });

    const handlePickNested = async (nestedGroup) => {
        const code = pickerFor.addonCode;
        setPickerFor(null);
        try {
            await modifierService.setAddonGroups(code, [nestedGroup.group_id]);
            setNestedOverrides(o => ({ ...o, [code]: nestedGroup }));
            toast && toast(t('nested_choice_added', {}, 'Nested choice added'), 'success');
        } catch {
            toast && toast(t('failed_to_save_group', {}, 'Failed to save group'), 'error');
        }
    };

    const removeNested = async (code) => {
        try {
            await modifierService.setAddonGroups(code, []);
            setNestedOverrides(o => ({ ...o, [code]: null }));
        } catch {
            toast && toast(t('failed_to_save_group', {}, 'Failed to save group'), 'error');
        }
    };

    const editNested = (code) => {
        const g = nestedGroupFor(code);
        if (g) { setDrillGroup(g); setDrillForCode(code); }
    };

    // Saving the drill-in editor must close only that layer, not the whole
    // stack. Calling the outer instance's own `onSaved` here would (at the
    // top level) close the outer editor entirely and discard any unsaved
    // outer-form text-field edits (name/receipt-name/rule/required) the
    // tenant had typed before drilling in. Instead: close just the drill
    // layer, then re-fetch the group list and patch this option's nested-
    // group override in place, so the nestbar reflects the drilled-in
    // group's new name/rule (or, if it was deleted from within the drill,
    // reverts to "no nested choice") — all without touching outer state.
    const handleDrillSaved = async () => {
        const code = drillForCode;
        const savedGroupId = drillGroup?.group_id;
        setDrillGroup(null);
        setDrillForCode(null);
        if (!code || !savedGroupId) return;
        try {
            const fresh = await modifierService.getGroups();
            const updated = fresh.find(g => g.group_id === savedGroupId) || null;
            setNestedOverrides(o => ({ ...o, [code]: updated }));
        } catch {
            // Best-effort refresh only — a later full reload (triggered by
            // this outer editor's own Save/Close) will still pick up the truth.
        }
    };

    const availableAddons = (addons || []).filter(a => !options.some(o => o.addon_product_code === a.product_code));

    const warnStyle = {
        display: 'flex', alignItems: 'center', gap: 9, padding: '11px 13px', borderRadius: 10,
        background: C.amberLight, border: `1px solid ${C.amberBorder}`, fontSize: 12.5, fontWeight: 700, marginBottom: 16,
    };

    const labelStyle = {
        fontSize: 10.5, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase',
        color: C.muted, display: 'block', marginBottom: 7,
    };

    const inputStyle = {
        width: '100%', height: 42, border: `1px solid ${C.border}`, borderRadius: 10,
        padding: '0 13px', background: C.white, font: 'inherit', fontSize: 13.5, boxSizing: 'border-box',
    };

    return (
        <>
        <div
            style={{
                position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.42)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={onClose}
        >
            <div
                className="portal-modal-card"
                style={{
                    background: C.white, borderRadius: 15, width: 'min(720px, calc(100vw - 40px))',
                    maxHeight: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column',
                    overflow: 'hidden', fontFamily: FONT, boxShadow: '0 24px 70px rgba(0,0,0,0.24)',
                }}
                onClick={e => e.stopPropagation()}
            >
                <div style={{
                    padding: '19px 24px 15px', borderBottom: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', gap: 12,
                }}>
                    <h3 style={{ fontSize: 17, fontWeight: 800, flex: 1, color: C.text, margin: 0 }}>
                        {group ? t('edit_group', {}, 'Edit group') : t('new_group_title', {}, 'New group')}
                        {group && <span style={{ fontWeight: 600, color: C.muted }}> · {group.name}</span>}
                    </h3>
                    <button
                        onClick={onClose}
                        aria-label={t('close', {}, 'Close')}
                        style={{
                            width: 32, height: 32, borderRadius: 9, background: C.bg, border: `1px solid ${C.border}`,
                            color: C.muted, display: 'grid', placeItems: 'center', fontSize: 16, cursor: 'pointer',
                        }}
                    >✕</button>
                </div>

                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px' }}>
                    {savedGroup && savedGroup.used_by_products > 1 && (
                        <div style={warnStyle}>
                            ⚠ {t('used_by_products_warning', { count: savedGroup.used_by_products },
                                `Used by ${savedGroup.used_by_products} products — anything you change here changes all of them.`)}
                        </div>
                    )}
                    {savedGroup && savedGroup.nested_in && (
                        <div style={{ ...warnStyle, background: C.blueLight, border: `1px solid ${C.blueBorder}`, color: C.blue }}>
                            ↳ {t('nested_in_group_warning', { name: savedGroup.nested_in },
                                `This group is nested inside ${savedGroup.nested_in}. It only appears when that option is chosen.`)}
                        </div>
                    )}

                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 190px', gap: 14, marginBottom: 19 }}>
                        <div>
                            <label style={labelStyle}>{t('group_name', {}, 'Group name')}</label>
                            <input
                                value={form.name}
                                onChange={e => set('name', e.target.value)}
                                style={inputStyle}
                                placeholder={t('eg_flavour_choice', {}, 'e.g. Corn flavour')}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('receipt_name', {}, 'Shown on receipt as')}</label>
                            <input
                                value={form.receiptName}
                                onChange={e => set('receiptName', e.target.value)}
                                style={inputStyle}
                                placeholder={form.name}
                            />
                        </div>
                    </div>

                    <label style={labelStyle}>{t('selection_rule', {}, 'Selection rule')}</label>
                    <div className="portal-wrap" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 11 }}>
                        {[
                            { mode: 'one', on: form.maxSelect === 1,
                                label: t('pick_exactly_1', {}, 'Pick exactly 1'), hint: t('pick_exactly_1_hint', {}, 'Radio buttons — one wins') },
                            { mode: 'upto', on: !!form.maxSelect && form.maxSelect > 1,
                                label: t('pick_up_to_n', { n: form.maxSelect > 1 ? form.maxSelect : 2 }, `Pick up to ${form.maxSelect > 1 ? form.maxSelect : 2}`), hint: t('pick_up_to_hint', {}, 'Capped multi-select') },
                            { mode: 'any', on: !form.maxSelect,
                                label: t('pick_any', {}, 'Pick any'), hint: t('pick_any_hint', {}, 'No limit') },
                        ].map(opt => (
                            <button
                                key={opt.mode}
                                onClick={() => set('maxSelect', opt.mode === 'one' ? 1 : opt.mode === 'any' ? null : (form.maxSelect > 1 ? form.maxSelect : 2))}
                                style={{
                                    padding: '10px 15px', borderRadius: 10, textAlign: 'left', fontSize: 12.5, fontWeight: 700,
                                    border: `1.5px solid ${opt.on ? C.green : C.border}`,
                                    background: opt.on ? C.greenLight : C.white,
                                    color: opt.on ? C.greenDark : C.muted, cursor: 'pointer',
                                }}
                            >
                                {opt.label}
                                <span style={{ display: 'block', fontSize: 10.5, fontWeight: 600, marginTop: 2, opacity: opt.on ? 0.75 : 1, color: opt.on ? C.greenDark : C.mutedLight }}>{opt.hint}</span>
                            </button>
                        ))}
                    </div>
                    {!!form.maxSelect && form.maxSelect > 1 && (
                        <div style={{ marginBottom: 11 }}>
                            <input
                                type="number" min={2} value={form.maxSelect}
                                onChange={e => set('maxSelect', Math.max(2, parseInt(e.target.value, 10) || 2))}
                                style={{ ...inputStyle, width: 90, height: 34 }}
                            />
                        </div>
                    )}

                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 11, padding: '11px 14px',
                        border: `1px solid ${C.border}`, borderRadius: 10, background: C.bg,
                        fontSize: 13, fontWeight: 700, marginBottom: 20,
                    }}>
                        <div>
                            {t('required', {}, 'Required')}
                            <div style={{ fontSize: 11.5, fontWeight: 600, color: C.muted }}>
                                {t('required_hint', {}, "Cashier can't add the item until this is answered")}
                            </div>
                        </div>
                        <button
                            role="switch"
                            aria-checked={form.required}
                            onClick={() => set('required', !form.required)}
                            style={{
                                width: 40, height: 23, borderRadius: 999, marginLeft: 'auto', border: 'none',
                                background: form.required ? C.green : C.border, position: 'relative', cursor: 'pointer', padding: 0,
                            }}
                        >
                            <span style={{
                                position: 'absolute', top: 2, left: form.required ? 19 : 2, width: 19, height: 19,
                                borderRadius: '50%', background: C.white, boxShadow: '0 1px 3px rgba(0,0,0,0.28)', transition: 'left 0.15s',
                            }} />
                        </button>
                    </div>

                    <label style={labelStyle}>{t('options_reorder_hint', {}, 'Options')}</label>
                    {options.map(o => {
                        const addon = addonMap[o.addon_product_code];
                        const nested = nestedGroupFor(o.addon_product_code);
                        return (
                            <div key={o.addon_product_code} style={{ marginBottom: 9 }}>
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px',
                                    border: `1px solid ${C.border}`, borderRadius: 11, background: C.white,
                                }}>
                                    <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: C.text }}>
                                        {addon ? addon.name : o.addon_product_code}
                                    </span>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: C.muted }}>
                                        {addon && addon.price ? `+${currency}${Number(addon.price).toFixed(2)}` : t('free', {}, 'Free')}
                                    </span>
                                    <button
                                        onClick={() => removeOption(o.addon_product_code)}
                                        aria-label={t('remove_option', {}, 'Remove option')}
                                        style={{
                                            width: 28, height: 28, borderRadius: 8, border: 'none', background: 'none',
                                            color: C.mutedLight, cursor: 'pointer', fontSize: 14,
                                        }}
                                    >✕</button>
                                </div>
                                {nested ? (
                                    <div style={{
                                        margin: '-3px 0 0 20px', display: 'flex', alignItems: 'center', gap: 9,
                                        padding: '9px 13px', borderInlineStart: `3px solid ${C.blue}`,
                                        background: C.blueLight, borderRadius: '0 10px 10px 0', fontSize: 12.5,
                                    }}>
                                        <span>↳</span>
                                        <span style={{ fontWeight: 800, color: C.blue, flex: 1 }}>{nested.name}</span>
                                        <span style={{ fontSize: 10, fontWeight: 700, color: C.blue, opacity: 0.8 }}>{ruleLabel(nested, t)}</span>
                                        <button onClick={() => editNested(o.addon_product_code)} style={{ fontSize: 11.5, fontWeight: 700, color: C.blue, background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer' }}>
                                            {t('edit')}
                                        </button>
                                        <button onClick={() => removeNested(o.addon_product_code)} style={{ fontSize: 11.5, fontWeight: 700, color: C.blue, background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer' }}>
                                            {t('remove', {}, 'Remove')}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => openNestPicker(o.addon_product_code)}
                                        style={{
                                            margin: '-3px 0 0 20px', fontSize: 11.5, fontWeight: 700, color: C.mutedLight,
                                            border: `1px dashed ${C.border}`, borderRadius: 9, padding: '7px 12px',
                                            width: 'calc(100% - 20px)', textAlign: 'left', background: 'none', cursor: 'pointer',
                                        }}
                                    >
                                        {t('add_nested_choice', { name: addon ? addon.name : o.addon_product_code },
                                            `＋ Add a nested choice to ${addon ? addon.name : o.addon_product_code}`)}
                                    </button>
                                )}
                            </div>
                        );
                    })}

                    {addingOption ? (
                        <div style={{ border: `1px dashed ${C.border}`, borderRadius: 10, padding: 10, marginTop: 6 }}>
                            {availableAddons.length === 0 ? (
                                <div style={{ fontSize: 12, color: C.muted }}>
                                    {t('no_more_addons_available', {}, 'All add-ons are already in this group.')}
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {availableAddons.map(a => (
                                        <button
                                            key={a.product_code}
                                            onClick={() => addOption(a.product_code)}
                                            style={{
                                                fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 8,
                                                border: `1px solid ${C.border}`, background: C.bg, color: C.text, cursor: 'pointer',
                                            }}
                                        >{a.name}</button>
                                    ))}
                                </div>
                            )}
                            <button onClick={() => setAddingOption(false)} style={{ marginTop: 8, fontSize: 12, color: C.muted, background: 'none', border: 'none', cursor: 'pointer' }}>
                                {t('cancel')}
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setAddingOption(true)}
                            style={{
                                width: '100%', marginTop: 6, padding: '9px 0', borderRadius: 10,
                                border: `1px solid ${C.border}`, background: C.white, color: C.muted,
                                fontSize: 13, fontWeight: 700, cursor: 'pointer',
                            }}
                        >
                            {t('add_option_from_library', {}, '＋ Add option from the add-on library')}
                        </button>
                    )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 24px', borderTop: `1px solid ${C.border}`, background: C.bg }}>
                    {groupId && (confirmingDelete ? (
                        <>
                            <span style={{ fontSize: 12, color: C.red, fontWeight: 700 }}>
                                {t('confirm_delete_group', {}, 'Delete this group?')}
                            </span>
                            <button onClick={handleDelete} disabled={saving} style={{ ...btnBase, background: C.red, color: C.white }}>
                                {t('delete')}
                            </button>
                            <button onClick={() => setConfirmingDelete(false)} style={{ ...btnBase, background: C.white, border: `1px solid ${C.border}`, color: C.muted }}>
                                {t('cancel')}
                            </button>
                        </>
                    ) : (
                        <button onClick={() => setConfirmingDelete(true)} style={{ ...btnBase, background: C.white, border: `1px solid ${C.redBorder}`, color: C.red }}>
                            {t('delete_group', {}, 'Delete group')}
                        </button>
                    ))}
                    <div style={{ flex: 1 }} />
                    <button onClick={onClose} style={{ ...btnBase, background: C.white, border: `1px solid ${C.border}`, color: C.muted }}>
                        {t('cancel')}
                    </button>
                    <button onClick={handleSave} disabled={saving} style={{ ...btnBase, background: C.green, color: C.white, opacity: saving ? 0.7 : 1 }}>
                        {saving ? `${t('saving')}…` : t('save_group', {}, 'Save group')}
                    </button>
                </div>
            </div>
        </div>

        {pickerFor && (
            <NestedGroupPicker
                open
                groups={pickerCandidates}
                optionName={pickerFor.addonName}
                onPick={handlePickNested}
                onClose={() => setPickerFor(null)}
                t={t}
            />
        )}

        {drillGroup && (
            <ModifierGroupEditor
                open
                group={drillGroup}
                allGroups={allGroups}
                addons={addons}
                currency={currency}
                onClose={() => { setDrillGroup(null); setDrillForCode(null); }}
                onSaved={handleDrillSaved}
                t={t}
                toast={toast}
            />
        )}
        </>
    );
}
